"use client";

import { useEffect, useRef } from "react";

export default function DitherTorusHero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    // Skip the dither torus animation on mobile. The 1200-point CPU rAF loop
    // is the main cost — keeping the empty <canvas> in the DOM avoids any
    // layout shift while freeing the main thread on small/low-power devices.
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = (canvas.width = canvas.offsetWidth);
    let height = (canvas.height = canvas.offsetHeight);

    // Geometry: Torus of points
    const points: { x: number; y: number; z: number }[] = [];
    const numPoints = 1200; // Denser for torus

    // Torus parameters
    const R = Math.min(width, height) * 0.25; // Major radius
    const r = Math.min(width, height) * 0.1; // Minor radius

    // Generate points
    for (let i = 0; i < numPoints; i++) {
      const u = Math.random() * Math.PI * 2;
      const v = Math.random() * Math.PI * 2;

      const x = (R + r * Math.cos(v)) * Math.cos(u);
      const y = (R + r * Math.cos(v)) * Math.sin(u);
      const z = r * Math.sin(v);

      points.push({ x, y, z });
    }

    let rotationX = 0;
    let rotationY = 0;
    let zoom = 1;
    const zoomSpeed = 0.0008;

    const bayerMatrix = [
      [0, 8, 2, 10],
      [12, 4, 14, 6],
      [3, 11, 1, 9],
      [15, 7, 13, 5],
    ];

    let animationId: number;

    const animate = () => {
      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, width, height);
      // Use a slightly different accent for the homepage? Or keep consistent orange.
      // Let's stick to the brand primary (orange) but maybe slightly brighter.
      ctx.fillStyle = "rgb(255, 140, 0)";

      rotationX += 0.002; // Faster rotation
      rotationY += 0.003;

      zoom = 1 + Math.sin(Date.now() * zoomSpeed) * 0.15;

      const cx = width * 0.5; // Center for homepage
      const cy = height * 0.5;

      points.forEach((p) => {
        // Rotate Y
        let x = p.x * Math.cos(rotationY) - p.z * Math.sin(rotationY);
        let z = p.x * Math.sin(rotationY) + p.z * Math.cos(rotationY);
        let y = p.y;

        // Rotate X
        const y_new = y * Math.cos(rotationX) - z * Math.sin(rotationX);
        z = y * Math.sin(rotationX) + z * Math.cos(rotationX);
        y = y_new;

        // Apply Zoom
        x *= zoom;
        y *= zoom;
        z *= zoom;

        // Perspective
        const scale = 1000 / (1000 + z);
        const x2d = x * scale + cx;
        const y2d = y * scale + cy;

        if (x2d < 0 || x2d > width || y2d < 0 || y2d > height) return;

        // Depth logic
        const depth = (z + R + r) / (2 * (R + r));
        const intensity = 1 - depth;

        // Dither
        const mx = Math.floor(x2d) % 4;
        const my = Math.floor(y2d) % 4;
        const threshold = bayerMatrix[my][mx] / 16;

        if (intensity > threshold - 0.1) {
          const pixelSize = 4;
          const xSnap = Math.floor(x2d / pixelSize) * pixelSize;
          const ySnap = Math.floor(y2d / pixelSize) * pixelSize;

          ctx.globalAlpha = 0.6 + intensity * 0.4;
          ctx.fillRect(xSnap, ySnap, pixelSize, pixelSize);
        }
      });

      animationId = requestAnimationFrame(animate);
    };

    const handleResize = () => {
      if (canvas) {
        width = canvas.width = canvas.offsetWidth;
        height = canvas.height = canvas.offsetHeight;
      }
    };

    window.addEventListener("resize", handleResize);
    animationId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none mix-blend-multiply dark:mix-blend-screen opacity-50"
    />
  );
}
